import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, RefreshCw, Save, X } from 'lucide-react'
import {
  Button,
  ConfirmDialog,
  Drawer,
  FormField,
  FormRow,
  FormSection,
  HelpTip,
  ProductPageShell,
  SegmentedControl,
  StatePanel,
} from '@/components/common'
import { DecisionReview } from '@/components/patterns'
import { useOrganization } from '@/contexts/OrganizationContext'
import { igamingApi } from '@/services/api'
import { CollapsibleFieldGroup } from './shared/CollapsibleFieldGroup'
import { DataList } from './shared/DataList'
import { RuntimeImpact } from './shared/RuntimeImpact'
import {
  DEFAULT_DATA_TAB,
  DEFAULT_METADATA_FORM,
  DEFAULT_SOURCE_FORM,
  FIELD_HELP,
  PAGE_COPY,
  PAYOUT_OPTIONS,
  SOURCE_STATUS_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  VOLATILITY_OPTIONS,
} from './shared/ruleConstants'
import { formatDate } from './shared/formatters'
import { buildMetadataImpact } from './shared/impactHelpers'
import {
  buildMetadataPayload,
  buildSourcePayload,
  metadataToForm,
  sourceToForm,
} from './shared/payloadBuilders'
import { buildPayloadChangeItems } from '@/utils/payloadReview'
import styles from './shared/igaming.module.css'

const METADATA_REVIEW_FIELDS = {
  game_code: { label: 'Game code', impact: 'Changes the game matched by this review' },
  game_name: { label: 'Game name', impact: 'Changes the operator-facing game identity' },
  status: { label: 'Review status', impact: 'Controls runtime eligibility' },
  'metadata.theme_tags': { label: 'Themes', impact: 'Changes search and browse matching' },
  'metadata.mechanic_tags': { label: 'Mechanics', impact: 'Changes feature-based discovery' },
  'metadata.volatility': { label: 'Volatility', impact: 'Changes volatility matching' },
  'metadata.payout_level': { label: 'Payout signal', impact: 'Changes payout-based ranking and filtering' },
  'metadata.min_bet': { label: 'Min bet', impact: 'Changes stake eligibility context' },
  'metadata.max_bet': { label: 'Max bet', impact: 'Changes stake eligibility context' },
  'metadata.currency': { label: 'Currency', impact: 'Changes how stake values are interpreted' },
  'metadata.max_win_multiplier': { label: 'Max win multiplier', impact: 'Changes max-win matching' },
  source_notes: { label: 'Source notes', impact: 'Changes the operator audit context' },
  is_active: { label: 'Active', impact: 'Controls inclusion in runtime metadata' },
}

const SOURCE_REVIEW_FIELDS = {
  name: { label: 'Source name', impact: 'Changes the operator-facing source identity' },
  source_type: { label: 'Source type', impact: 'Changes the source lifecycle classification' },
  status: { label: 'Status', impact: 'Controls source lifecycle state' },
  owner: { label: 'Owner', impact: 'Changes operational responsibility' },
  mapping_notes: { label: 'Mapping notes', impact: 'Changes source mapping instructions' },
  last_ingested_at: { label: 'Last imported', impact: 'Changes import recency context' },
  market: { label: 'Regulation', impact: 'Changes regulation scope' },
  segment: { label: 'Segment', impact: 'Changes player segment scope' },
  is_active: { label: 'Active', impact: 'Controls inclusion in source configuration' },
}

function queryReviewState(query) {
  if (query.isLoading) return 'loading'
  if (query.isError) return 'unavailable'
  if (query.data === undefined || query.data === null) return 'unknown'
  return 'ready'
}

function IGamingDataPage() {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const [metadataForm, setMetadataForm] = useState(DEFAULT_METADATA_FORM)
  const [sourceForm, setSourceForm] = useState(DEFAULT_SOURCE_FORM)
  const [dataError, setDataError] = useState('')
  const [dataTab, setDataTab] = useState(DEFAULT_DATA_TAB)
  const [deleteCandidate, setDeleteCandidate] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)

  const canConfigure = hasProductAccess('igaming_search', 'configure')
  const pageCopy = PAGE_COPY.data

  const runtimeQuery = useQuery({
    queryKey: ['igaming', slug, 'runtime'],
    queryFn: () => igamingApi.getRuntimeConfig(slug),
    enabled: Boolean(slug),
  })

  const metadataQuery = useQuery({
    queryKey: ['igaming', slug, 'metadata-reviews'],
    queryFn: () => igamingApi.listMetadataReviews(slug),
    enabled: Boolean(slug),
  })

  const sourcesQuery = useQuery({
    queryKey: ['igaming', slug, 'data-sources'],
    queryFn: () => igamingApi.listDataSources(slug),
    enabled: Boolean(slug),
  })

  const saveMetadata = useMutation({
    mutationFn: (payload) => metadataForm.id
      ? igamingApi.updateMetadataReview(slug, metadataForm.id, payload)
      : igamingApi.createMetadataReview(slug, payload),
    onSuccess: () => {
      toast.success('Metadata review saved')
      setMetadataForm(DEFAULT_METADATA_FORM)
      setEditorOpen(false)
      setDataError('')
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => setDataError(error.response?.data?.error || error.message || 'Failed to save metadata review'),
  })

  const deleteMetadata = useMutation({
    mutationFn: (id) => igamingApi.deleteMetadataReview(slug, id),
    onSuccess: () => {
      toast.success('Metadata review deleted')
      setMetadataForm(DEFAULT_METADATA_FORM)
      setDeleteCandidate(null)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to delete metadata review'),
  })

  const saveSource = useMutation({
    mutationFn: (payload) => sourceForm.id
      ? igamingApi.updateDataSource(slug, sourceForm.id, payload)
      : igamingApi.createDataSource(slug, payload),
    onSuccess: () => {
      toast.success('Data source saved')
      setSourceForm(DEFAULT_SOURCE_FORM)
      setEditorOpen(false)
      setDataError('')
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => setDataError(error.response?.data?.error || error.message || 'Failed to save data source'),
  })

  const deleteSource = useMutation({
    mutationFn: (id) => igamingApi.deleteDataSource(slug, id),
    onSuccess: () => {
      toast.success('Data source deleted')
      setSourceForm(DEFAULT_SOURCE_FORM)
      setDeleteCandidate(null)
      queryClient.invalidateQueries({ queryKey: ['igaming', slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to delete data source'),
  })

  const runtime = runtimeQuery.data || null
  const metadataReviews = metadataQuery.data?.items || []
  const dataSources = sourcesQuery.data?.items || []
  const currentMetadata = metadataReviews.find((item) => item.id === metadataForm.id) || null
  const currentSource = dataSources.find((item) => item.id === sourceForm.id) || null
  const metadataProposal = buildMetadataPayload(metadataForm)
  const sourceProposal = buildSourcePayload(sourceForm)
  const metadataChanges = buildPayloadChangeItems({
    current: currentMetadata ? buildMetadataPayload(metadataToForm(currentMetadata)) : null,
    proposed: metadataProposal,
    fieldMetadata: METADATA_REVIEW_FIELDS,
    ignorePaths: ['confidence'],
    defaultImpact: 'Changes player-facing metadata or its review record',
  })
  const sourceChanges = buildPayloadChangeItems({
    current: currentSource ? buildSourcePayload(sourceToForm(currentSource)) : null,
    proposed: sourceProposal,
    fieldMetadata: SOURCE_REVIEW_FIELDS,
    ignorePaths: ['config', 'schedule_config'],
    defaultImpact: 'Changes the submitted source definition',
  })
  const metadataImpact = buildMetadataImpact(metadataProposal, runtime)
  const runtimeReviewState = queryReviewState(runtimeQuery)
  const runtimePayloadReviewValue = {
    loading: 'Loading',
    unavailable: 'Unavailable',
    unknown: 'Unknown',
  }[runtimeReviewState]
  const runtimeReviewConsequence = {
    loading: 'Runtime inclusion is still being checked.',
    unavailable: 'Runtime inclusion is unavailable. Retry the runtime configuration before relying on this review.',
    unknown: 'Runtime inclusion is unknown because runtime configuration has not loaded.',
  }[runtimeReviewState]
  const deletePending = deleteMetadata.isPending || deleteSource.isPending

  const dataTabs = [
    { key: 'metadata', label: 'Metadata', count: metadataReviews.length },
    { key: 'sources', label: 'Sources', count: dataSources.length },
  ]

  const submitDataForm = (event, mutation, payloadBuilder, formState, requiredFields) => {
    event.preventDefault()
    const missingField = requiredFields.find((field) => !String(formState[field] || '').trim())
    if (missingField) {
      setDataError('Fill in the required fields before saving.')
      return
    }
    mutation.mutate(payloadBuilder(formState))
  }

  return (
    <ProductPageShell
      title={pageCopy.title}
      subtitle={pageCopy.subtitle}
      actions={(
        <>
          <Button variant="secondary" onClick={() => {
            runtimeQuery.refetch()
            metadataQuery.refetch()
            sourcesQuery.refetch()
          }}>
            <RefreshCw size={16} />
            Refresh
          </Button>
          {canConfigure ? (
            <Button onClick={() => {
              setDataError('')
              if (dataTab === 'metadata') setMetadataForm(DEFAULT_METADATA_FORM)
              else setSourceForm(DEFAULT_SOURCE_FORM)
              setEditorOpen(true)
            }}>
              <Plus size={16} />
              {dataTab === 'metadata' ? 'Create metadata review' : 'Create data source'}
            </Button>
          ) : null}
        </>
      )}
    >
      <div className={styles.stack}>
        {!canConfigure ? (
          <StatePanel
            state="restricted"
            title="Read-only iGaming data"
            description="Configure access is required to create, edit, or delete metadata reviews and data sources."
            compact
          />
        ) : null}
        <SegmentedControl
          items={dataTabs}
          value={dataTab}
          label="iGaming data type"
          onValueChange={(tab) => {
            setDataError('')
            setDataTab(tab)
          }}
        />
        {runtimeQuery.isError ? (
          <StatePanel
            state="error"
            title="Runtime configuration is unavailable"
            description={runtimeQuery.error?.response?.data?.error || runtimeQuery.error?.message}
            onRetry={() => runtimeQuery.refetch()}
            compact
          />
        ) : null}
        {dataTab === 'metadata' && metadataQuery.isLoading ? (
          <StatePanel state="loading" title="Loading metadata reviews" compact />
        ) : null}
        {dataTab === 'metadata' && metadataQuery.isError ? (
          <StatePanel
            state="error"
            title="Metadata reviews failed"
            description={metadataQuery.error?.response?.data?.error || metadataQuery.error?.message}
            onRetry={() => metadataQuery.refetch()}
            compact
          />
        ) : null}
        {dataTab === 'sources' && sourcesQuery.isLoading ? (
          <StatePanel state="loading" title="Loading data sources" compact />
        ) : null}
        {dataTab === 'sources' && sourcesQuery.isError ? (
          <StatePanel
            state="error"
            title="Data sources failed"
            description={sourcesQuery.error?.response?.data?.error || sourcesQuery.error?.message}
            onRetry={() => sourcesQuery.refetch()}
            compact
          />
        ) : null}
        {dataTab === 'metadata' && (
          <div className={styles.stack}>
            <Drawer
              open={canConfigure && editorOpen && dataTab === 'metadata'}
              side="right"
              title={metadataForm.id ? 'Edit metadata review' : 'Create metadata review'}
              description="Review game metadata and its player-facing runtime impact before saving."
              onClose={() => {
                setEditorOpen(false)
                setMetadataForm(DEFAULT_METADATA_FORM)
                setDataError('')
              }}
            >
              <FormSection title="Metadata details">
                <form className={styles.form} onSubmit={(event) => submitDataForm(event, saveMetadata, buildMetadataPayload, metadataForm, ['game_code'])}>
                  <FormRow>
                    <FormField label="Game code" required value={metadataForm.game_code} placeholder="provider_game_code" onChange={(event) => setMetadataForm((current) => ({ ...current, game_code: event.target.value }))} />
                    <FormField label="Game name" value={metadataForm.game_name} placeholder="Optional display name" onChange={(event) => setMetadataForm((current) => ({ ...current, game_name: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Themes" labelExtra={<HelpTip text={FIELD_HELP.themes} label="Themes help" />} value={metadataForm.themes} placeholder="irish, ancient egypt, fishing" onChange={(event) => setMetadataForm((current) => ({ ...current, themes: event.target.value }))} />
                    <FormField label="Mechanics" labelExtra={<HelpTip text="Comma-separated mechanics, e.g. free spins, bonus buy, cascading reels." label="Mechanics help" />} value={metadataForm.mechanics} placeholder="free spins, bonus buy" onChange={(event) => setMetadataForm((current) => ({ ...current, mechanics: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Volatility" type="select" value={metadataForm.volatility} options={VOLATILITY_OPTIONS} onChange={(event) => setMetadataForm((current) => ({ ...current, volatility: event.target.value }))} />
                    <FormField label="Payout signal" type="select" value={metadataForm.payout_level} options={PAYOUT_OPTIONS} onChange={(event) => setMetadataForm((current) => ({ ...current, payout_level: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Max win multiplier" type="number" value={metadataForm.max_win_multiplier} placeholder="10000" onChange={(event) => setMetadataForm((current) => ({ ...current, max_win_multiplier: event.target.value }))} />
                    <FormField label="Status" type="select" value={metadataForm.status} options={[{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'overridden', label: 'Overridden' }, { value: 'rejected', label: 'Rejected' }]} onChange={(event) => setMetadataForm((current) => ({ ...current, status: event.target.value }))} />
                  </FormRow>
                  <FormField label="Source notes" value={metadataForm.source_notes} placeholder="Why this tag should be trusted or changed" onChange={(event) => setMetadataForm((current) => ({ ...current, source_notes: event.target.value }))} />
                  <CollapsibleFieldGroup title="Stake details">
                    <FormRow>
                      <FormField label="Min bet" type="number" value={metadataForm.min_bet} placeholder="0.20" onChange={(event) => setMetadataForm((current) => ({ ...current, min_bet: event.target.value }))} />
                      <FormField label="Max bet" type="number" value={metadataForm.max_bet} placeholder="100" onChange={(event) => setMetadataForm((current) => ({ ...current, max_bet: event.target.value }))} />
                    </FormRow>
                    <FormRow>
                      <FormField label="Currency" value={metadataForm.currency} placeholder="USD" onChange={(event) => setMetadataForm((current) => ({ ...current, currency: event.target.value }))} />
                      <div />
                    </FormRow>
                  </CollapsibleFieldGroup>
                  {metadataForm.game_code.trim() ? (
                    <DecisionReview
                      title={metadataForm.id ? 'Review metadata changes' : 'Review new metadata'}
                      description="Confirm the operator decision and whether it can enter the player-facing runtime."
                      contextItems={[
                        { label: 'Game code', value: metadataProposal.game_code },
                        { label: 'Runtime status', value: metadataImpact.status },
                        { label: 'Runtime payload', value: runtimePayloadReviewValue || (metadataImpact.rules.length ? metadataImpact.rules.join(', ') : 'Not currently included') },
                      ]}
                      changeItems={metadataChanges}
                      consequence={runtimeReviewConsequence || (metadataImpact.warnings.length
                        ? <ul className={styles.impactWarnings}>{metadataImpact.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                        : 'Saving keeps this game eligible for the next runtime metadata payload.')}
                      error={dataError}
                      actions={(
                        <>
                          {metadataForm.id ? <Button type="button" variant="secondary" onClick={() => setMetadataForm(DEFAULT_METADATA_FORM)}><X size={16} />Cancel</Button> : null}
                          <Button type="submit" disabled={saveMetadata.isPending}><Save size={16} />Save metadata</Button>
                        </>
                      )}
                    />
                  ) : (
                    <div className={styles.formActions}>
                      {dataError ? <p className={styles.error} role="alert">{dataError}</p> : null}
                      {metadataForm.id ? <Button type="button" variant="secondary" onClick={() => setMetadataForm(DEFAULT_METADATA_FORM)}><X size={16} />Cancel</Button> : null}
                      <Button type="submit" disabled={saveMetadata.isPending}><Save size={16} />Save metadata</Button>
                    </div>
                  )}
                </form>
              </FormSection>
            </Drawer>
            <FormSection title="Metadata reviews">
              <DataList
                items={metadataReviews}
                primary={(item) => item.game_name || item.game_code}
                meta={(item) => [item.status, item.metadata?.volatility, item.metadata?.payout_level ? `Payout ${item.metadata.payout_level}` : '', item.updated_at ? formatDate(item.updated_at) : '']}
                renderDetails={(item) => <RuntimeImpact impact={buildMetadataImpact(item, runtime)} />}
                onEdit={canConfigure ? (item) => {
                  setMetadataForm(metadataToForm(item))
                  setEditorOpen(true)
                } : undefined}
                onDelete={canConfigure ? (id) => setDeleteCandidate({ kind: 'metadata', item: metadataReviews.find((entry) => entry.id === id) }) : undefined}
                deletePending={deleteMetadata.isPending}
              />
            </FormSection>
          </div>
        )}

        {dataTab === 'sources' && (
          <div className={styles.stack}>
            <Drawer
              open={canConfigure && editorOpen && dataTab === 'sources'}
              side="right"
              title={sourceForm.id ? 'Edit data source' : 'Create data source'}
              description="Define ownership and lifecycle state before saving the source."
              onClose={() => {
                setEditorOpen(false)
                setSourceForm(DEFAULT_SOURCE_FORM)
                setDataError('')
              }}
            >
              <FormSection title="Source details">
                <form className={styles.form} onSubmit={(event) => submitDataForm(event, saveSource, buildSourcePayload, sourceForm, ['name'])}>
                  <FormRow>
                    <FormField label="Source name" required value={sourceForm.name} placeholder="Manual promo sheet" onChange={(event) => setSourceForm((current) => ({ ...current, name: event.target.value }))} />
                    <FormField label="Source type" type="select" value={sourceForm.source_type} options={SOURCE_TYPE_OPTIONS} onChange={(event) => setSourceForm((current) => ({ ...current, source_type: event.target.value }))} />
                  </FormRow>
                  <FormRow>
                    <FormField label="Owner" value={sourceForm.owner} placeholder="Operations, Commercial, Data" onChange={(event) => setSourceForm((current) => ({ ...current, owner: event.target.value }))} />
                    <FormField label="Status" type="select" value={sourceForm.status} options={SOURCE_STATUS_OPTIONS} onChange={(event) => setSourceForm((current) => ({ ...current, status: event.target.value }))} />
                  </FormRow>
                  <FormField label="Last imported" type="datetime-local" value={sourceForm.last_ingested_at} onChange={(event) => setSourceForm((current) => ({ ...current, last_ingested_at: event.target.value }))} />
                  <CollapsibleFieldGroup title="Mapping notes">
                    <FormField label="Notes" value={sourceForm.mapping_notes} placeholder="Game IDs come from provider_game_code; promo type maps from campaign_type." onChange={(event) => setSourceForm((current) => ({ ...current, mapping_notes: event.target.value }))} />
                  </CollapsibleFieldGroup>
                  <CollapsibleFieldGroup title="Advanced">
                    <FormRow>
                      <FormField label="Regulation" value={sourceForm.market} placeholder="NJ" onChange={(event) => setSourceForm((current) => ({ ...current, market: event.target.value }))} />
                      <FormField label="Segment" value={sourceForm.segment} placeholder="vip" onChange={(event) => setSourceForm((current) => ({ ...current, segment: event.target.value }))} />
                    </FormRow>
                  </CollapsibleFieldGroup>
                  {sourceForm.name.trim() ? (
                    <DecisionReview
                      title={sourceForm.id ? 'Review source changes' : 'Review new source'}
                      description="Confirm ownership and lifecycle state before saving the source definition."
                      contextItems={[
                        { label: 'Source type', value: sourceProposal.source_type },
                        { label: 'Owner', value: sourceProposal.owner || 'Unassigned' },
                        { label: 'Last imported', value: currentSource?.last_ingested_at ? formatDate(currentSource.last_ingested_at) : 'Never imported' },
                      ]}
                      changeItems={sourceChanges}
                      consequence="Saving updates the source definition only. It does not start an import or ingest new catalog data."
                      error={dataError}
                      actions={(
                        <>
                          {sourceForm.id ? <Button type="button" variant="secondary" onClick={() => setSourceForm(DEFAULT_SOURCE_FORM)}><X size={16} />Cancel</Button> : null}
                          <Button type="submit" disabled={saveSource.isPending}><Save size={16} />Save source</Button>
                        </>
                      )}
                    />
                  ) : (
                    <div className={styles.formActions}>
                      {dataError ? <p className={styles.error} role="alert">{dataError}</p> : null}
                      {sourceForm.id ? <Button type="button" variant="secondary" onClick={() => setSourceForm(DEFAULT_SOURCE_FORM)}><X size={16} />Cancel</Button> : null}
                      <Button type="submit" disabled={saveSource.isPending}><Save size={16} />Save source</Button>
                    </div>
                  )}
                </form>
              </FormSection>
            </Drawer>
            <FormSection title="Configured data sources">
              <DataList
                items={dataSources}
                primary={(item) => item.name}
                meta={(item) => [item.source_type, item.status, item.owner, item.last_ingested_at ? formatDate(item.last_ingested_at) : 'Never imported']}
                onEdit={canConfigure ? (item) => {
                  setSourceForm(sourceToForm(item))
                  setEditorOpen(true)
                } : undefined}
                onDelete={canConfigure ? (id) => setDeleteCandidate({ kind: 'source', item: dataSources.find((entry) => entry.id === id) }) : undefined}
                deletePending={deleteSource.isPending}
              />
            </FormSection>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(deleteCandidate?.item)}
        title={`Delete ${deleteCandidate?.kind === 'source' ? 'data source' : 'metadata review'}`}
        message={`Delete "${deleteCandidate?.item?.name || deleteCandidate?.item?.game_name || deleteCandidate?.item?.game_code || 'this item'}"? This permanently removes it from Studio and the next runtime configuration.`}
        confirmLabel={`Delete ${deleteCandidate?.kind === 'source' ? 'data source' : 'metadata review'}`}
        cancelLabel="Keep item"
        loading={deletePending}
        onConfirm={() => {
          if (!deleteCandidate?.item || deletePending) return
          if (deleteCandidate.kind === 'source') deleteSource.mutate(deleteCandidate.item.id)
          else deleteMetadata.mutate(deleteCandidate.item.id)
        }}
        onCancel={() => {
          if (!deletePending) setDeleteCandidate(null)
        }}
        isDestructive
      />
    </ProductPageShell>
  )
}

export default IGamingDataPage
